<template>
  <div v-clickoutside="$_onClickOutside"
       :class="dropdownClasses"
  >
    <div ref="trigger"
         name="trigger"
         @click.stop="$_onTriggerClick"
         @mouseenter="$_onTriggerMouseEnter"
         @mouseleave="$_onTriggerMouseLeave"
    >
      <slot name="trigger"></slot>
    </div>
    <transition name="fade">
      <div v-show="currentVisibled"
           ref="popContent"
           v-detach="popper"
           :class="dropdownClasses+'__content'"
           :style="popperStyle"
      >
        <slot name="popper"></slot>
      </div>
    </transition>
  </div>
</template>
<script>
import Popper from 'popper.js'
import { PREFIX_CLASS } from '../../utils/constants'
import clickoutside from '../../directives/click-outside'
import Detach from '../../directives/detacher'

const PREFIXCLS = `${PREFIX_CLASS}dropdown`
const HIDE_DELAY = 150 // 隐藏延迟毫秒数
const MODE_CLICK = 'click'
const MODE_HOVER = 'hover'
const MODE_CUSTOM = 'custom'
let prevInstance = null

export default {
  name: 'TDropdown',
  directives: {
    clickoutside,
    Detach
  },
  props: {
    triggerMode: {
      type: String,
      validator(value) {
        return [MODE_CLICK, MODE_HOVER, MODE_CUSTOM].includes(value)
      },
      default: 'hover'
    },
    visibled: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    position: {
      type: String,
      validator(value) {
        /*
         * 有以下值：
         * 'top',
         * 'top-start',
         * 'top-end',
         * 'bottom',
         * 'bottom-start',
         * 'bottom-end',
         * 'left',
         * 'left-start',
         * 'left-end',
         * 'right',
         * 'right-start',
         * 'right-end'
         * https://popper.js.org/popper-documentation.html#Popper.placements
         */
        return Popper.placements.includes(value)
      },
      default: 'bottom-start'
    },
    // 偏移量同Popper.js的offset选项: https://popper.js.org/popper-documentation.html#modifiers..offset
    offset: {
      type: [String, Number],
      default: 0
    }
  },
  data() {
    return {
      minWidth: '',
      leaveTimer: null,
      popper: null,
      currentVisibled: false,
      currentDisabled: false
    }
  },
  computed: {
    popperStyle() {
      return {
        'min-width': this.minWidth
      }
    },
    dropdownClasses() {
      return [
        `${PREFIXCLS}`
      ]
    }
  },
  watch: {
    visibled(val) {
      this.currentVisibled = val
      if (this.triggerMode === MODE_CUSTOM && val) {
        this.popper.update()
      }
    },
    disabled(val) {
      this.currentDisabled = val
    }
  },
  created() {
    this.currentDisabled = this.disabled
  },
  updated() {
    this.$_updateMinWidth()
  },
  mounted() {
    let opts = {
      placement: this.position
    }
    if (this.offset !== 0) {
      opts.modifiers = {
        offset: {
          offset: this.offset
        }
      }
    }

    this.popper = new Popper(this.$refs.trigger, this.$refs.popContent, opts)
    // this.currentVisibled = true
    this.$_updateMinWidth()
  },
  beforeDestroy() {
    this.$_detachEvents() // 卸载事件处理函数
    prevInstance = null // 释放引用
    // 清除计时器
    if (this.leaveTimer !== null) {
      window.clearTimeout(this.leaveTimer)
    }
    // 销毁Popper实例
    if (this.popper) {
      this.popper.destroy()
      this.popper = null
    }
  },
  methods: {
    $_updateMinWidth() {
      this.minWidth = window.getComputedStyle(this.$refs.trigger).getPropertyValue('width')
    },
    updatePosition() {
      if (this.currentVisibled === false) {
        this.$nextTick(() => {
          this.popper.update()
        })
      }
    },
    // 一个页面内某一时刻仅显示一个实例的弹出内容
    resetPrevInstance() {
      if (prevInstance !== null && prevInstance !== this) {
        prevInstance.$_detachEvents()
        prevInstance.currentVisibled = false
        prevInstance.$emit('update:visibled', false)
      }
      prevInstance = this
    },
    $_onClickOutside (event) {
      if (this.currentVisibled === true) {
        if (this.triggerMode === MODE_CUSTOM) return false
        if (this.triggerMode !== MODE_CLICK) {
          return false
        }
        if (this.triggerMode === MODE_CLICK &&
          event.target.className === 'dropdown__item') {
          return false
        }
        this.currentVisibled = false
        this.$emit('update:visibled', false)
      }
    },
    $_onTriggerClick() {
      this.resetPrevInstance()
      if (this.triggerMode !== MODE_CLICK || this.currentDisabled) {
        return false
      }
      this.updatePosition()
      this.currentVisibled = !this.currentVisibled
      this.$emit('update:visibled', this.currentVisibled)
    },
    // 弹出内容由于需要从原有位置移动到body元素下，因此直接在模板里绑定的DOM事件会被
    // 自动卸载掉，所以需要动态添加DOM事件
    $_attachEvents () {
      this.$refs.popContent.addEventListener('mouseenter', this.$_onPopperMouseEnter)
      this.$refs.popContent.addEventListener('mouseleave', this.$_onPopperMouseLeave)
    },
    $_detachEvents () {
      this.$refs.popContent.removeEventListener('mouseenter', this.$_onPopperMouseEnter)
      this.$refs.popContent.removeEventListener('mouseleave', this.$_onPopperMouseLeave)
    },
    $_onTriggerMouseEnter() {
      if (this.currentVisibled === false) {
        if (this.triggerMode !== MODE_HOVER || this.currentDisabled) {
          return false
        }
        this.resetPrevInstance()
        this.currentVisibled = true
        this.popper.update()
        this.$emit('update:visibled', true)
        this.$_attachEvents()
      }
    },
    $_onTriggerMouseLeave () {
      if (this.currentVisibled) {
        if (this.triggerMode !== MODE_HOVER || this.currentDisabled) {
          return false
        }
        this.leaveTimer = window.setTimeout(() => {
          this.$_detachEvents()
          this.currentVisibled = false
          this.$emit('update:visibled', false)
        }, HIDE_DELAY)
      }
    },
    $_onPopperMouseEnter() {
      if (this.triggerMode === MODE_HOVER) {
        window.clearTimeout(this.leaveTimer)
      }
    },
    $_onPopperMouseLeave() {
      if (this.currentVisibled === true) {
        if (this.triggerMode !== MODE_HOVER) {
          return false
        }
        this.leaveTimer = window.setTimeout(() => {
          this.$_detachEvents()
          this.currentVisibled = false
          this.$emit('update:visibled', false)
        }, HIDE_DELAY)
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/module/dropdown/dropdown.vue